'use client';

import { useCallback, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useDrawer } from '@cat-ecom/pcc-components';
import { CatButton } from 'components/blocks-components';
import { useCreateListModal } from 'providers/create-list-modal-provider';
import { useNavigationDrawers } from 'app/[lang]/[esite]/(core)/_header/components/navigation-drawers-context';
import { CreateListModal, SavedListDrawer } from 'components/saved-lists';
import { OPEN_BUTTON_TEXT } from 'components/saved-lists/create-list-modal/constants/common';
import {
  addPartToSavedList,
  createSavedLists
} from 'services/savedLists/actions';
import { LIST_TYPES } from 'constants/common';
import { uploadedFiles } from 'components/saved-lists/create-list-modal/types';
import { MyEquipmentList } from 'components/my-equipment/types';
import { useEsiteCtx } from 'services/esite/context';
import { useQuickToast } from 'hooks/use-toast';
import { RowSelectionState } from '@tanstack/react-table';
import { SavedList } from 'services/savedLists/types';
import { ProductPriceAvailability } from 'services/priceAvailability/types';
import { Size, Variant } from 'components/contact-your-dealer/types';

interface SavedListButtonProps {
  part: ProductPriceAvailability;
  locale: string;
  isLoggedIn?: boolean;
  savedLists: SavedList[];
  userHasShoppingLists?: boolean;
  isCspUser?: boolean;
  isDealerUser?: boolean;
  isInstantAccessUser?: boolean;
  isAssociateEquipmentToSavedList: boolean;
  myEquipmentList: MyEquipmentList[];
  selectedEquipmentIndex: number;
  isError: boolean;
  variant?: Variant;
  size?: Size;
  className?: string;
  withMobileFixedActions?: boolean;
  showAllList?: boolean;
  fullWidth?: boolean;
  saveToListEnhancement?: boolean;
  savedListDetailFeatureFlag?: boolean;
}

const SavedListButton = ({
  part,
  locale,
  isLoggedIn = false,
  userHasShoppingLists = false,
  isCspUser = false,
  isInstantAccessUser,
  isDealerUser,
  isAssociateEquipmentToSavedList,
  myEquipmentList,
  selectedEquipmentIndex,
  isError,
  savedLists,
  variant,
  size = 'md',
  className = '',
  withMobileFixedActions = false,
  showAllList,
  fullWidth = false,
  saveToListEnhancement = false,
  savedListDetailFeatureFlag = false
}: SavedListButtonProps) => {
  const t = useTranslations();
  const { storeId } = useEsiteCtx();

  const { accountDrawer } = useNavigationDrawers();
  const { drawerProps, closeDrawer, openDrawer, isDrawerOpen } = useDrawer();
  const { openModal } = useCreateListModal() || {};
  const toast = useQuickToast();

  const handleClick = () => {
    if (!isLoggedIn) {
      saveToListEnhancement &&
        sessionStorage.setItem('OPEN_SAVE_LIST_MODEL', 'saveListModel');
      if (!accountDrawer?.isDrawerOpen) {
        accountDrawer?.openDrawer();
      }
      return;
    }
    openAppropriateDrawer();
  };

  useEffect(() => {
    if (saveToListEnhancement) {
      const getSaveListKey = sessionStorage.getItem('OPEN_SAVE_LIST_MODEL');
      if (getSaveListKey === 'saveListModel' && isLoggedIn) {
        openAppropriateDrawer();
        sessionStorage.removeItem('OPEN_SAVE_LIST_MODEL');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAppropriateDrawer = () => {
    if (userHasShoppingLists) {
      openDrawer();
    } else {
      openModal();
    }
  };

  const createAction = async (
    type: boolean,
    name: string,
    file: uploadedFiles,
    equipment: MyEquipmentList
  ) => {
    try {
      await createSavedLists({
        storeId,
        locale,
        body: {
          type: type ? LIST_TYPES.SHARED : LIST_TYPES.PRIVATE,
          name: name,
          orderItems: [
            {
              quantity: part?.quantity,
              partNumber: part?.partNumber,
              name: part?.name
            }
          ],
          equipment
        }
      });
      toast.success({
        message: t('SAVE_ITEM_TO_LIST_CART_BLOCKS', {
          '0': name
        })
      });
      return {
        status: 'Success',
        error: false
      };
    } catch (error) {
      return {
        status: 'Error',
        error: true
      };
    }
  };

  const commonProps = {
    createAction,
    isInstantAccessUser,
    isDealerUser,
    isAssociateEquipmentToSavedList,
    myEquipmentList,
    selectedEquipmentIndex,
    isError,
    withMobileFixedActions,
    savedListDetailFeatureFlag
  };

  const addItemToListHandler = useCallback(
    async (savedListRowSelected: RowSelectionState) => {
      const selectedListId = Object.keys(savedListRowSelected)[0];
      const { name } = savedLists.find(
        ({ shoppingListId }) => shoppingListId === parseInt(selectedListId)
      );
      await addPartToSavedList({
        orderItems: [
          {
            quantity: part?.quantity,
            partNumber: part?.partNumber
          }
        ],
        shoppingListId: selectedListId,
        locale,
        storeId
      });
      try {
        toast.success({
          message: t('SAVE_ITEM_TO_LIST_CART_BLOCKS', {
            '0': name
          })
        });

        document?.body?.style.removeProperty('overflow');
      } catch (error) {
        console.log(error);
      }

      closeDrawer();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closeDrawer, t, toast]
  );

  const onFailHandler = useCallback(() => {
    toast.error({
      message: t('QO_UNKNOWNERROR_TITLE')
    });
  }, [toast, t]);

  return (
    <>
      <CatButton
        {...(variant && { variant })}
        data-testid="SaveToListButton"
        onClick={handleClick}
        size={size}
        className={className}
        fullWidth={fullWidth}
      >
        {t('SAVE_TO_LIST')}
      </CatButton>
      {isDrawerOpen && (
        <SavedListDrawer
          drawerProps={drawerProps}
          closeDrawer={closeDrawer}
          onActionDrawer={addItemToListHandler}
          onFailHandler={onFailHandler}
          isCspUser={false}
          drawerTitle={t('SAVE_ITEM_TO_LIST_CART')}
          confirmBtnText={t('SAVE_WISHLIST')}
          hideBackButton
          showCreateListBtn
          openButtonText={OPEN_BUTTON_TEXT}
          {...commonProps}
          showAllList={showAllList}
        />
      )}
      <CreateListModal
        isCspUser={isCspUser}
        showCreateListBtn={false}
        {...commonProps}
      />
    </>
  );
};

export default SavedListButton;
